import { z } from 'zod';
import { Ulid, UserIdentity } from './common.js';
import { PLAY_EVENT_DEDUPE_MINUTES } from './store.js';
/**
 * Game analytics (`docs/analytics-plan.md`).
 *
 * Sibling to `feedback.ts`, and for the same reason: several things that are not this module
 * have to agree on these numbers — the dashboard, the Worker, and (from AN2) the injected
 * Java and JS layers, whose constants are **generated** from here rather than hand-copied.
 *
 * The module is deliberately whole while the feature is not. AN0 and AN1 use the settings and
 * presence halves; the session half below is the vocabulary AN2's ingest route and injected
 * layer will be built against, and it is written down once, here, rather than invented twice.
 */
/* ------------------------------------------------------------------------------ settings */
/**
 * Whether a channel reports analytics.
 *
 * **A channel decides, and there is nothing above it** — the same shape `feedback_layer_settings`
 * has, and now for the same reason. This began as a game-level answer that channels could
 * override, which made a channel row three states rather than two: on, off, and "whatever the
 * game says". That third state bought a `Follow game` button, a resolution walk every reader had
 * to know about, and a class of bug where a value resolves back to a default the moment somebody
 * moves the default. It is gone. A channel says yes or it says no.
 */
export const AnalyticsSettings = z.object({
    /**
     * Presence: which build is on which Board, and every install, update and removal. Costs a build
     * nothing — no patching, no re-signing, the same bytes the developer uploaded.
     *
     * Reported by the BoardFlight desktop app since 2026-10-11 (`docs/off-board-plan.md` §6): its
     * sync of what is on an account's Boards, and the installs it performs. Nothing on a Board
     * reports any of it, so a game somebody installed by hand through the Board's own page is on no
     * record at all.
     */
    enabled: z.boolean(),
    /**
     * The in-game session layer: how long the game is actually played (§5).
     *
     * **A second switch, and it is deliberately not the same one.** §0.1 proposed one setting driving
     * both, and that was written before every app gained a `public` channel with analytics on
     * (§14.12). Under one switch, `shouldPatchBuild` would then re-sign every build published to
     * `public`, on every game — and a signing change costs every tester an uninstall and their saved
     * data (feedback-plan §3.4). Presence is free and sessions are not, so the expensive half asks
     * separately and defaults to off — **except on a web game's `public` channel**, where it is on
     * until the team says otherwise (`sessionsDefault` in `feedback.ts`, off-board-plan decision 12):
     * a web build is patched without being re-signed, and the library's ranking stands on it.
     *
     * Resolves to false whenever `enabled` is false: the session layer is part of analytics, so
     * turning analytics off turns everything off rather than leaving a build reporting.
     */
    sessionsEnabled: z.boolean(),
    /**
     * Custom game events (`docs/game-events-plan.md` §8.1): what the game's own code reports.
     *
     * The end of a chain — **events ⇒ sessions ⇒ presence**. Per-session rates, playtime
     * normalisation and `quit` detection all need play sessions, and both need the patch anyway, so
     * a channel with events and no sessions would cost the same re-sign and answer less. Resolves to
     * false whenever `sessionsEnabled` does. Off by default everywhere, `playground` included
     * (decision 12).
     */
    eventsEnabled: z.boolean(),
});
/**
 * A channel's answer, or off.
 *
 * Not a walk any more — there is one row to consult and no row means off — but still the only
 * place the two invariants are written down: absence is `false`, and `sessionsEnabled` is
 * `&& enabled`. The settings route, the presence gate, the ingest route and the config endpoint
 * all call it, because two answers to "is this build reporting?" means one of them is collecting
 * data somebody switched off.
 *
 * Off by default is the whole promise of the feature, and `null` is what a channel nobody has
 * touched looks like.
 */
export function resolveAnalytics(channel) {
    const enabled = channel?.enabled ?? false;
    // `&& enabled`, so there is no such state as "sessions on, analytics off". Without it a
    // developer who switched analytics off would keep every patched build on every Board
    // reporting sessions, which is the opposite of what that switch means.
    const sessionsEnabled = enabled && (channel?.sessionsEnabled ?? false);
    return {
        enabled,
        sessionsEnabled,
        // The same rule one link further down the chain: no events without sessions.
        eventsEnabled: sessionsEnabled && (channel?.eventsEnabled ?? false),
    };
}
/** A settings write. One channel, named, every time. */
export const UpdateAnalyticsRequest = z.object({
    /**
     * Required. There is no game-level row to fall back to when this is absent, and defaulting to
     * one would be inventing the level this feature deliberately does not have.
     */
    channelId: Ulid,
    /**
     * Absent means "leave it alone" — the two switches are written by the same route and a body
     * that names one must not reset the other. The route reads the raw body for key presence rather
     * than the parsed object, the same way `UpdateFeedbackLayerRequest` is read.
     *
     * `null` is **not** a value here. It used to mean "stop overriding and follow the game"; with
     * nothing to follow, a nullable switch would only be a way to write a row that says nothing.
     */
    enabled: z.boolean().optional(),
    /**
     * The session layer. Turning it on for a channel is what makes builds published there get
     * patched and re-signed, which is why the dashboard asks first.
     */
    sessionsEnabled: z.boolean().optional(),
    /**
     * Custom game events. Turning them on turns on sessions and presence with them — the chain in
     * `resolveAnalytics`, written by the route rather than left dormant — so it patches and re-signs
     * exactly as the session switch does, and the dashboard asks first for the same reason. Turning
     * sessions or presence off turns these off.
     */
    eventsEnabled: z.boolean().optional(),
});
/** One channel's two switches, as the settings screen shows them. */
export const AnalyticsChannelSettings = z.object({
    channelId: Ulid,
    channelName: z.string(),
    channelSlug: z.string(),
    /** This channel's own answer. False for a channel nobody has decided about. */
    enabled: z.boolean(),
    /** Already `&& enabled` — see `resolveAnalytics`. */
    sessionsEnabled: z.boolean(),
    /**
     * Already `&& sessionsEnabled`. **Absent, not false, for a team without `analytics.events`**: a
     * response must not tell a team about a capability it does not have (`CLAUDE.md`, plans are
     * invisible), so the field exists only where the switch does.
     */
    eventsEnabled: z.boolean().optional(),
    updatedAt: z.number().int().nullable(),
});
export const AnalyticsSettingsResponse = z.object({
    /**
     * Every channel of the game, in channel order, decided or not. Every channel, not only the ones
     * carrying a row — a settings screen is a list of decisions, and a channel missing from it is
     * one nobody will think to look at.
     */
    channels: z.array(AnalyticsChannelSettings),
});
/**
 * What analytics records, in the words a tester would be owed — `docs/analytics-plan.md` §9.2's
 * sentence, kept here so the panel and the doc cannot drift apart.
 *
 * Split in two because the two halves are switched on separately and cost different things. The
 * presence half applies to any game with analytics on; the session half applies only where the
 * session switch is on, because only that one puts code inside the running game. Saying both
 * unconditionally would describe collection that is not happening — which, in a privacy
 * disclosure, is the worst direction to be wrong. §9.2's single sentence is the two joined, which
 * is what `ANALYTICS_DISCLOSURE` is.
 *
 * **Who reports the presence half changed on 2026-10-11** (`docs/off-board-plan.md` §6). It used to
 * say BoardFlight's app on the Board recorded what was installed there; that app is gone, and the
 * only thing that reads a Board now is the BoardFlight desktop app on a tester's own computer — if
 * they use one. The sentence says "connected to their account" rather than naming that app, which
 * nobody can get yet (`docs/off-board-plan.md` §0: copy describes what exists), and says what
 * follows from it: a tester who installs
 * through the Board's own page is recorded nowhere. The session half gained the one thing a
 * session start now also is — a play, for the library's ranking, kept under a hash of the network
 * address it came from (`docs/store-plan.md` §5).
 */
export const ANALYTICS_DISCLOSURE_PRESENCE = 'When a tester’s Board has been connected to their BoardFlight account, BoardFlight records ' +
    'which of your builds is on it, and when one arrives, changes or is removed, against the ' +
    'tester’s account and that Board. Nothing on a Board reports this: a game installed by hand ' +
    'through the Board’s own page is not recorded at all.';
export const ANALYTICS_DISCLOSURE_SESSIONS = 'Where play sessions are switched on, it also records when the game starts and stops, its ' +
    'build id, device model, OS version, locale and timezone, under a random install id. Each ' +
    'start also counts as one play towards the library’s ranking, at most once every ' +
    `${PLAY_EVENT_DEDUPE_MINUTES} minutes from one network address, which is kept only as a ` +
    'hash. It never records gameplay, input, or screen contents.';
export const ANALYTICS_DISCLOSURE = `${ANALYTICS_DISCLOSURE_PRESENCE} ${ANALYTICS_DISCLOSURE_SESSIONS}`;
/** How long raw sessions and presence events are kept before the sweep takes them (§9.1). */
export const ANALYTICS_RETENTION_DAYS = 180;
/* ------------------------------------------------------------------------------ presence */
/** Where a row in the Install activity feed came from (§8.3). */
export const ActivitySource = z.enum(['install', 'presence']);
/**
 * One row of the merged Install activity feed.
 *
 * Two kinds of row in one stream, discriminated by `source`. Almost everything is nullable
 * because a presence row can name a build this server can no longer resolve — a sideloaded
 * copy, or one from before the game was on BoardFlight — and that is an honest answer rather
 * than a missing one.
 */
export const ActivityEvent = z.object({
    id: Ulid,
    source: ActivitySource,
    /**
     * An `InstallStatus` for an install row. For a presence row, what a sync's diff observed, third
     * person: `installed_seen`, `updated_seen` or `uninstalled_seen` (`presence_events.kind`, written
     * by `PUT /v1/desktop/boards/:fingerprint`). Deliberately not new `install_events` statuses:
     * those rows are the first-person account of an install BoardFlight ran, and carry Board's own
     * message; these are observations of a device — an install done through Board's own page, an
     * uninstall nobody told us about.
     */
    status: z.string(),
    /** Board's own words. Only ever on an install row; presence observes, it does not quote. */
    boardMessage: z.string().nullable(),
    createdAt: z.number().int(),
    buildId: Ulid.nullable(),
    versionName: z.string().nullable(),
    versionCode: z.number().int().nullable(),
    buildNumber: z.number().int().nullable(),
    channelSlug: z.string().nullable(),
    /** Who. A handle always; a name and an address only for someone in the team reading this. */
    ...UserIdentity.shape,
    /**
     * Which of this person's Boards, as an opaque id and nothing more. There is no device name:
     * Board's own is its owner's Board username, which a studio outside their team must not see
     * (`DeviceIdentity` in `events.ts`).
     */
    deviceId: Ulid.nullable(),
});
/* ----------------------------------------------------------------------------- adoption */
/**
 * One tester's Board, and what it has of this game (§7.5).
 *
 * As the BoardFlight desktop app last read it off that Board (`board_apps`, off-board-plan §6) —
 * so a tester with no desktop app has no row, whatever is on their Board.
 */
export const AdoptionEntry = z.object({
    ...UserIdentity.shape,
    /**
     * Which of this person's Boards, as an opaque id and nothing more — `boards.id`. Never its name:
     * a Board's name is its owner's, shown to that account and to nobody else (`CLAUDE.md`).
     */
    deviceId: Ulid,
    /** What the Board reported. Null for a web app, which Board reports no version for. */
    versionCode: z.number().int().nullable(),
    versionName: z.string().nullable(),
    /**
     * The build those bytes are, when this server can tell. Null is common and honest: a
     * sideloaded copy, a build since pruned, or a web app with no version to match on.
     */
    buildId: Ulid.nullable(),
    buildVersionName: z.string().nullable(),
    buildNumber: z.number().int().nullable(),
    channelSlug: z.string().nullable(),
    firstSeenAt: z.number().int(),
    lastSeenAt: z.number().int(),
    /** Set once the game stopped being on this Board. The row is kept; the history is the point. */
    uninstalledAt: z.number().int().nullable(),
});
export const AdoptionResponse = z.object({
    /**
     * Whether analytics is on anywhere on this game. An empty list means two different things —
     * "switched off, so nothing is collected" and "on, and no Board has reported yet" — and the
     * view says something different for each.
     */
    enabled: z.boolean(),
    items: z.array(AdoptionEntry),
});
/* ------------------------------------------------------------------------------ sessions */
/*
 * Everything below is AN2's wire format. Nothing reads it yet — there is no ingest route and no
 * injected analytics layer — and it is here rather than in the commit that builds them because
 * the Java constants are generated from this file and the plan (§2) says so once.
 */
/**
 * A UUID as the injected layer mints it.
 *
 * A deliberate break from this repo's rule that ids are ULIDs and `ORDER BY id` is chronological.
 * The layer is dependency-free Java 8 compiled with `javac`/`d8`: `UUID.randomUUID()` is in the
 * platform, and a hand-rolled Crockford base32 ULID is exactly the kind of primitive this repo
 * refuses to hand-roll.
 *
 * The consequence, written down so nobody trips on it: **nothing may order `play_sessions` by
 * `id`.** Every query orders by `started_at`, and the migration's indexes are built for that.
 */
export const Uuid = z
    .string()
    .regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, 'Not a UUID.');
/**
 * Seconds between heartbeats while the game is foregrounded. Served by the config endpoint so
 * it can be tuned without re-patching; this is the baked default.
 */
export const ANALYTICS_HEARTBEAT_SECONDS = 300;
/**
 * A session backgrounded and re-foregrounded within this window is one session, not two. Board
 * games get interrupted by the system UI; a 20-second detour is not "played twice".
 */
export const SESSION_RESUME_GRACE_SECONDS = 60;
/**
 * The server closes an open session whose `last_seen_at` is older than this. A fixed window,
 * deliberately not derived from `heartbeatSeconds`: the configured interval can change while
 * sessions are open, and a sweep that guesses which interval a given row was using closes live
 * sessions. 15 min ≥ any interval this plan allows (`heartbeatSeconds` ≤ 600).
 */
export const SESSION_STALE_MINUTES = 15;
export const SessionEndReason = z.enum(['background', 'timeout']);
/** Ingest limits — mirrored in the layer's constants and the route's checks alike. */
export const MAX_SESSION_EVENTS_PER_REQUEST = 20;
/** Duration clamp. A session longer than this is a clock, not a play session. */
export const MAX_SESSION_HOURS = 24;
export const MAX_EVENTS_PER_INSTALL_PER_HOUR = 120;
/**
 * How far a device's clock may disagree with the server's before a request's timestamps are moved.
 *
 * A layer that sends `sentAt` — its own clock at the moment it sent — tells the server how wrong
 * that clock is, and every `at` in the same request is on the same clock. Past this tolerance the
 * whole request is shifted by the difference, in either direction: a Board that booted offline
 * with a clock days behind would otherwise file a session on the wrong day, and one ahead would
 * have it pulled back to the moment it arrived. Inside it the device's word stands — a request in
 * flight for a few seconds is not a wrong clock. Shared by session and game-event ingest.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 60 * 1000;
/**
 * The offset to add to every timestamp of a request, given the device's `sentAt` and the server's
 * `now`: zero when the two agree within `CLOCK_SKEW_TOLERANCE_MS` or the layer is too old to say.
 */
export function clockSkew(sentAt, now) {
    if (typeof sentAt !== 'number' || !Number.isFinite(sentAt))
        return 0;
    const skew = now - sentAt;
    return Math.abs(skew) > CLOCK_SKEW_TOLERANCE_MS ? skew : 0;
}
/**
 * What the injected layer is told, inside `GET /v1/feedback-layer/config`'s `analytics` block
 * (§7.2). One endpoint, one cached fetch in the layer, one ETag.
 *
 * `enabled` here is the **session** switch, not the presence one. The layer only ever does
 * sessions; presence is the client's job and the layer has no business knowing whether it is on.
 *
 * This block is what makes the kill switch work: a build already on fifty Boards stops reporting
 * within the config TTL, without re-patching anything. Already-patched builds are safe against
 * this field appearing, and that is verified rather than hoped — the shipped layer parses with
 * `optBoolean`/`opt*` and ignores keys it has never heard of.
 */
export const AnalyticsLayerConfig = z.object({
    enabled: z.boolean(),
    heartbeatSeconds: z.number().int().positive(),
});
export const SessionEventType = z.enum(['start', 'heartbeat', 'end']);
export const SessionEvent = z.object({
    type: SessionEventType,
    /** Epoch ms on the device's clock, trusted for nothing and clamped server-side. */
    at: z.number().int(),
    endReason: SessionEndReason.optional(),
});
/**
 * One request = one session's events plus enough metadata to create the row from ANY event.
 *
 * This is the load-bearing shape decision: a heartbeat can arrive before its `start` — a retry, a
 * reorder, a start lost to a dead radio — and a row with NOT NULL columns cannot be created from
 * `{sessionId, type, at}` alone. So the metadata rides the envelope, not the start event.
 */
export const AnalyticsIngestRequest = z.object({
    /** The game's ingest key, baked into the build. An identifier, not a credential. */
    key: z.string().min(1).max(200),
    /** Baked at patch time, so a session names the precise build it came from. */
    buildId: Ulid,
    sessionId: Uuid,
    installId: Uuid,
    platform: z.enum(['unity', 'web']),
    meta: z.object({
        versionCode: z.number().int().optional(),
        versionName: z.string().max(64).optional(),
        deviceModel: z.string().max(64).optional(),
        osVersion: z.string().max(64).optional(),
        locale: z.string().max(32).optional(),
        tzOffsetMin: z.number().int().min(-900).max(900).optional(),
    }),
    events: z.array(SessionEvent).min(1).max(MAX_SESSION_EVENTS_PER_REQUEST),
    /**
     * The device's clock when this request was sent, on the same clock as every `at` above. Optional
     * because layers patched before it existed do not send it; see `clockSkew`.
     */
    sentAt: z.number().int().optional(),
});
/* ------------------------------------------------------------------------- the reading end */
/**
 * One UTC day of a game's numbers (§7.4). For one channel, a closed day is the rollup's row. Without
 * `?channel=`, a day the raw rows still cover is computed from them across channels; an older day is
 * the rollup's sums, with a median only where one channel played.
 */
export const AnalyticsDay = z.object({
    day: z.string(),
    sessions: z.number().int(),
    playtimeMs: z.number().int(),
    medianSessionMs: z.number().int().nullable(),
    activeInstalls: z.number().int(),
    installsSeen: z.number().int(),
    uninstallsSeen: z.number().int(),
});
/**
 * When people play, on their own clock.
 *
 * `tz_offset_min` on a session is what makes this answerable at all: "Tuesday at 8pm" is a fact
 * about the player's evening, and a heatmap drawn on the server's UTC clock would smear a European
 * playtest across two rows and call it a finding.
 *
 * `weekday` is 0 = Sunday, matching `strftime('%w')`, which is where it comes from.
 */
export const AnalyticsHourCell = z.object({
    weekday: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    sessions: z.number().int(),
});
export const AnalyticsSummary = z.object({
    /**
     * Whether anything is being collected at all. An empty summary means two different things —
     * switched off, versus on and nobody has played — and the tab says something different for each.
     */
    enabled: z.boolean(),
    /** Whether any channel has the in-game layer on. Without it there are never any sessions. */
    sessionsEnabled: z.boolean(),
    /** Every day in the requested range, including the empty ones — a gap is a fact. */
    days: z.array(AnalyticsDay),
    hours: z.array(AnalyticsHourCell),
    totals: z.object({
        sessions: z.number().int(),
        playtimeMs: z.number().int(),
        /** Distinct install ids that played in the range. */
        activeInstalls: z.number().int(),
        /** Across the whole range, not an average of daily medians — see the route. */
        medianSessionMs: z.number().int().nullable(),
        installsSeen: z.number().int(),
        uninstallsSeen: z.number().int(),
    }),
});
/** One play session, as the raw list shows it. */
export const PlaySession = z.object({
    id: Uuid,
    installId: Uuid,
    buildId: Ulid.nullable(),
    buildVersionName: z.string().nullable(),
    buildVersionCode: z.number().int().nullable(),
    channelSlug: z.string().nullable(),
    platform: z.enum(['unity', 'web']),
    deviceModel: z.string().nullable(),
    osVersion: z.string().nullable(),
    locale: z.string().nullable(),
    startedAt: z.number().int(),
    /** Null while it is still open — or still believed to be. */
    endedAt: z.number().int().nullable(),
    endReason: SessionEndReason.nullable(),
    /** Null for an open session. Derived, so the dashboard never subtracts two numbers itself. */
    durationMs: z.number().int().nullable(),
    /**
     * When the server last heard from this session, on the **server's** clock — what the stale sweep
     * goes by. Null for a session recorded before the column existed (migration 0073).
     */
    lastHeardAt: z.number().int().nullable(),
});
/**
 * One player, as the playtest view sees them.
 *
 * **An install id, not a person.** Sessions are anonymous by construction: the layer mints a random
 * UUID per install and BoardFlight has nothing to join it to. `displayName` is filled only where
 * attribution supplied a `user_id`, which is nothing today and is optional forever.
 *
 * It is emphatically **not** joined to `board_apps` (or `app_presence` before it) to work out who a
 * Board belongs to. That join is sitting right there — same app, same account's Board, overlapping
 * times — and taking it would de-anonymize every session in the system without anybody deciding
 * to. §10 forbids it, the route says so in a comment, and a test asserts it.
 */
export const AnalyticsTester = z.object({
    installId: Uuid,
    /** Null unless attribution named them, which is every row until AN5. */
    userId: Ulid.nullable(),
    /** Null exactly when `userId` is — there is no account to have a handle. */
    username: z.string().nullable(),
    /** Null unless attribution named them *and* they are in the team reading this. */
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    sessions: z.number().int(),
    playtimeMs: z.number().int(),
    lastPlayedAt: z.number().int(),
    deviceModel: z.string().nullable(),
});
//# sourceMappingURL=analytics.js.map